import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, ArrowLeft, Camera, Wifi } from "lucide-react";
import { Link } from "react-router-dom";
import { toBlob } from "html-to-image";
import { api, API, LOGO_URL } from "../lib/api";
import { useLang } from "../i18n";

const CTA = {
  wifi: { ru: "Бесплатный Wi-Fi", uz: "Bepul Wi-Fi", en: "Free Wi-Fi" },
  promo: { ru: "Скидку и промокод", uz: "Chegirma va promokod", en: "A discount promo code" },
  survey: { ru: "Подарок за опрос", uz: "So'rov uchun sovg'a", en: "A gift for feedback" },
  loyalty: { ru: "Кофе в подарок", uz: "Sovg'a kofe", en: "Free coffee rewards" },
  review: { ru: "Подарок за отзыв", uz: "Sharh uchun sovg'a", en: "A gift for your review" },
  lead: { ru: "Персональные предложения", uz: "Shaxsiy takliflar", en: "Personal offers" },
};

export default function Flyer() {
  const { slug } = useParams();
  const { L } = useLang();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);
  const [logoSrc, setLogoSrc] = useState(LOGO_URL);
  const canvasRef = useRef(null);
  useEffect(() => {
    api.get(`/public/campaign/${slug}`, { params: { scan: false } }).then((r) => setC(r.data.campaign)).catch(() => setErr(true));
    // inline the logo as data-URL so html-to-image export is cross-origin safe
    fetch(LOGO_URL).then((r) => r.blob()).then((b) => {
      const fr = new FileReader();
      fr.onload = () => setLogoSrc(fr.result);
      fr.readAsDataURL(b);
    }).catch(() => {});
  }, [slug]);

  const download = async () => {
    const node = canvasRef.current;
    if (!node || busy) return;
    setBusy(true);
    try {
      try { await document.fonts.ready; } catch (e) { /* older browsers */ }
      const imgs = Array.from(node.querySelectorAll("img"));
      await Promise.all(imgs.map((img) => (img.complete && img.naturalWidth > 0)
        ? Promise.resolve()
        : new Promise((res) => { img.onload = res; img.onerror = res; setTimeout(res, 4000); })));
      await new Promise((r) => setTimeout(r, 100));
      const blob = await toBlob(node, { pixelRatio: 2, width: node.offsetWidth, height: node.offsetHeight, style: { margin: "0" } });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CRAFT-FLYER-${slug}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      alert("Не удалось создать изображение, попробуйте ещё раз");
    }
    setBusy(false);
  };

  if (err) return <div className="min-h-screen flex items-center justify-center font-bold">Кампания не найдена</div>;
  if (!c) return <div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-[#FEBC10] border-t-transparent rounded-full animate-spin" /></div>;
  const cta = CTA[c.type] || CTA.lead;
  const isWifi = c.type === "wifi";
  const toolbar = (
    <div className="no-print flex items-center gap-3 w-full max-w-[720px]">
      <Link to="/admin/campaigns" className="p-2.5 bg-white/10 rounded-xl text-white" data-testid="flyer-back"><ArrowLeft size={16} /></Link>
      <div className="text-white font-display font-black flex-1">Флаер: {c.name?.ru}</div>
      <button data-testid="flyer-download-btn" onClick={download} disabled={busy} className="admin-btn"><Download size={15} /> {busy ? "Готовим..." : "Скачать"}</button>
    </div>
  );

  if (isWifi) {
    return (
      <div className="min-h-screen bg-[#2a2a2a] flex flex-col items-center py-8 px-4 gap-5">
        {toolbar}
        {/* A6 (105×148 мм) — красный постер Wi-Fi */}
        <div ref={canvasRef} data-testid="flyer-canvas"
          className="flyer-print w-[min(94vw,480px)] aspect-[105/148] relative overflow-hidden flex flex-col items-center pb-[16%] shadow-2xl"
          style={{ background: "radial-gradient(120% 90% at 70% 20%, #FF2A2A 0%, #E10514 42%, #B4000E 100%)" }}>
          {/* глянцевые блики */}
          <div className="absolute -right-10 top-6 w-40 h-40 rounded-full bg-white/10 blur-xl" />
          <div className="absolute right-2 top-1/3 w-24 h-40 rounded-full bg-orange-300/20 blur-2xl" />
          <div className="absolute -left-8 top-1/4 w-24 h-24 rounded-full bg-white/5 blur-lg" />

          {/* Шапка: логотип + бренд (как на других флаерах) */}
          <div className="mt-[5%] flex items-center justify-center gap-[3%] px-[6%]">
            <img src={logoSrc} alt="" className="w-[15%] min-w-[52px] aspect-square rounded-2xl shadow-lg shrink-0" />
            <div className="font-display font-black uppercase text-black text-left tracking-tight leading-[0.82]">
              <div className="text-[clamp(28px,7vw,46px)]">CRAFT</div>
              <div className="text-[clamp(15px,3.6vw,24px)] tracking-[0.16em] leading-none">Fast Food</div>
            </div>
          </div>

          {/* Заголовок */}
          <div className="mt-[3%] text-center px-[8%]">
            <div className="font-display font-black uppercase text-white text-[clamp(15px,4.2vw,26px)] leading-[1.05] drop-shadow">Сканируй и получи</div>
            <div className="font-display font-black uppercase text-white text-[clamp(13px,3.6vw,22px)] leading-none mt-[1%] drop-shadow">Бесплатный Wi-Fi</div>
          </div>

          {/* Плашка CRAFT WI-FI со значком Wi-Fi */}
          <div className="mt-[2.5%] bg-black rounded-full px-[6%] py-[1.6%] inline-flex items-center gap-2 whitespace-nowrap">
            <Wifi className="w-[clamp(15px,3.4vw,22px)] h-auto text-[#FEBC10] shrink-0" strokeWidth={2.5} />
            <span className="font-display font-black uppercase tracking-wide text-[#FEBC10] text-[clamp(13px,3.6vw,22px)] whitespace-nowrap">CRAFT WI-FI</span>
          </div>

          {/* QR */}
          <div className="mt-[3%] bg-white rounded-[1.4rem] p-[3%] shadow-[0_18px_50px_-12px_rgba(0,0,0,0.5)]">
            <img src={`${API}/qr/campaign/${c.slug}.png`} alt="QR" className="w-[clamp(118px,28vw,150px)] aspect-square block" />
          </div>

          {/* Подсказка: наведи камеру и отсканируй QR */}
          <div className="mt-[2.8%] inline-flex items-center gap-[2.5%] bg-black text-white px-[4.5%] py-[1.3%] rounded-full font-display font-black uppercase text-[clamp(9px,2vw,14px)] tracking-wide whitespace-nowrap">
            <Camera className="w-[clamp(13px,2.6vw,18px)] h-auto shrink-0" strokeWidth={2.5} /> Наведите камеру и отсканируйте QR
          </div>

          {/* Значок Wi-Fi (как на фото) — над жёлтым подвалом */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-[13.5%]" style={{ filter: "drop-shadow(0 0 14px rgba(255,120,170,0.95))" }}>
            <Wifi className="w-[clamp(52px,14vw,82px)] h-auto text-[#FF8FC0]" strokeWidth={2.6} />
          </div>

          {/* Жёлтый подвал */}
          <div className="absolute bottom-0 inset-x-0 bg-[#FEBC10] py-[3%] flex items-center justify-center gap-[2.5%]">
            <img src={logoSrc} alt="" className="w-[8%] min-w-[34px] aspect-square rounded-lg" />
            <span className="text-black font-display font-black text-[clamp(18px,4.6vw,30px)] tracking-wide">craft-oila.uz</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#2a2a2a] flex flex-col items-center py-8 px-4 gap-5">
      {toolbar}
      <div ref={canvasRef} className="flyer-print w-[min(94vw,720px)] aspect-square bg-[#FEBC10] relative overflow-hidden flex flex-col items-center shadow-2xl" data-testid="flyer-canvas">
        <div className="absolute -left-16 -top-16 w-48 h-48 rounded-full bg-[#FF0000]/15" />
        <div className="absolute -right-20 top-1/3 w-56 h-56 rounded-full bg-black/5" />
        <div className="absolute -left-12 bottom-[18%] w-40 h-40 rounded-full bg-white/20" />
        {/* Шапка: логотип + бренд по центру */}
        <div className="w-full flex items-center justify-center gap-[2.5%] pt-[4.5%] px-[6%]">
          <img src={logoSrc} alt="" className="w-[10%] min-w-[56px] aspect-square rounded-2xl shadow-lg shrink-0" />
          <div className="font-display font-black uppercase leading-[0.95] text-black text-[clamp(24px,5.2vw,42px)] tracking-tight text-left">
            CRAFT<br /><span className="text-[#FF0000] text-[0.62em] tracking-widest">fast food</span>
          </div>
        </div>
        <div className="mt-[2.5%] bg-black text-[#FEBC10] px-[5%] py-[1.5%] rounded-full font-display font-black uppercase text-[clamp(14px,3.2vw,26px)] max-w-[88%] text-center leading-tight">
          {L(c.name)}
        </div>
        <div className="mt-[3%] bg-white rounded-[2rem] p-[2.5%] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.35)]">
          <img src={`${API}/qr/campaign/${c.slug}.png`} alt="QR" className="w-[clamp(160px,30vw,250px)] aspect-square" />
        </div>
        <div className="mt-[3%] text-center px-6">
          <div className="font-display font-black uppercase text-black text-[clamp(16px,3.6vw,28px)] leading-none">Сканируй и получи</div>
          <div className="font-display font-extrabold uppercase text-[#FF0000] text-[clamp(14px,3vw,22px)] mt-1">{L(cta)}</div>
          <div className="mt-[2%] inline-flex items-center gap-[2%] bg-black text-white px-[4%] py-[1.2%] rounded-full font-display font-black uppercase text-[clamp(10px,1.9vw,16px)] tracking-wide whitespace-nowrap">
            <Camera className="w-[clamp(14px,2.4vw,22px)] h-auto shrink-0" strokeWidth={2.5} /> Наведите камеру на QR-код
          </div>
        </div>
        {/* Подвал: домен по центру */}
        <div className="absolute bottom-0 inset-x-0 bg-[#FF0000] py-[2.4%] flex items-center justify-center gap-[2%]">
          <img src={logoSrc} alt="" className="w-[5%] min-w-[30px] aspect-square rounded-lg" />
          <span className="text-white font-display font-black text-[clamp(16px,3.4vw,28px)] tracking-wide">craft-oila.uz</span>
        </div>
      </div>
    </div>
  );
}
